import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../lib/api.js'

/**
 * THE RECENT WINDOW — what concluded lately, and did the sweep actually run.
 *
 * Deliberately NOT `useChampionships`. That hook follows the cursor to
 * exhaustion, roughly 128 sequential calls for 12,740 records, because the map
 * and the rep books genuinely need the whole plane. This one asks the server a
 * narrow question and answers it in a single request, so opening the board no
 * longer costs the universe.
 *
 * Brian's framing: "it's Monday, there was a big weekend. Did the 7am search
 * trigger, and how do the weekend's championships look?" Two questions, one
 * call, and neither of them is "load everything and filter in the browser".
 *
 * Read-only. Never writes.
 */
export function useChampionWeek(days = 3) {
  const [data, setData] = useState(null)
  const [status, setStatus] = useState('loading') // loading | done | error
  const [error, setError] = useState('')
  const runId = useRef(0)

  const load = useCallback(
    async (signal) => {
      const rid = ++runId.current
      setStatus('loading')
      setError('')
      try {
        const res = await api.championWeek(days, { signal })
        if (signal?.aborted || rid !== runId.current) return
        setData(res)
        setStatus('done')
      } catch (err) {
        if (err?.name === 'AbortError' || signal?.aborted || rid !== runId.current) return
        setError(err?.message || 'Could not load the recent window.')
        setStatus('error')
      }
    },
    [days]
  )

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  const reload = useCallback(() => {
    const controller = new AbortController()
    load(controller.signal)
  }, [load])

  return { data, status, error, reload }
}

/* ---- HOW TO READ THE RUN RECEIPT ---------------------------------------
 * Three states, and collapsing any two of them is how a dead scheduled job
 * stays dead — which has already happened twice on this system.
 *
 *   'worked'   detection ran recently AND stamped records in the window
 *   'quiet'    detection ran recently but this window had nothing to stamp
 *   'silent'   nothing has stamped these records at all — the job did not fire
 *
 * 'silent' is an OPS problem, not a data problem, and it must never be
 * presented as "no champions this week". Those look identical on a board and
 * have completely different fixes.
 * ---------------------------------------------------------------------- */
export function runState(detection, concludedCount) {
  if (!detection || !detection.lastDetectionAt) {
    return concludedCount > 0 ? 'silent' : 'unknown'
  }
  if (detection.recordsStamped > 0) return 'worked'
  return 'quiet'
}

/** Machine or person, for the last-search line on a record. */
export function actorLabel(by, byKind) {
  if (!by) return null
  if (byKind === 'service') return by.replace(/^svc:/, '') + ' (automated)'
  if (byKind === 'unknown') return 'unattributed'
  return by
}

/** "2 hours ago" / "3 days ago" — relative time without pulling in a library. */
export function ago(iso, now = Date.now()) {
  const t = Date.parse(iso || '')
  if (!Number.isFinite(t)) return null
  const mins = Math.round((now - t) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs} ${hrs === 1 ? 'hour' : 'hours'} ago`
  const days = Math.round(hrs / 24)
  return `${days} ${days === 1 ? 'day' : 'days'} ago`
}
